import { useEffect, useState, useCallback, useMemo } from 'react';
import useCrmStore from '../stores/crmStore';
import LeadDetailPanel from '../components/LeadDetailPanel';
import NewLeadModal from '../components/NewLeadModal';
import { Search, Filter, Plus, Eye, Download, ChevronLeft, ChevronRight } from 'lucide-react';

const STAGE_LABELS = { nuevo_lead: 'Nuevo Lead', contactado: 'Contactado', en_seguimiento: 'En Seguimiento', promesa_pago: 'Promesa Pago', cerrado_ganado: 'Cerrado/Ganado', cerrado_perdido: 'Cerrado/Perdido' };
const ORIGENES = ['Referido', 'Redes sociales', 'Pagina web', 'Evento', 'Llamada entrante', 'WhatsApp'];

function ContactsToolbar({ search, setSearch, filterStage, setFilterStage, filterOrigen, setFilterOrigen, setPage, onExport, onNewLead, onSearch }) {
  return (
    <div className="contacts-toolbar">
      <form onSubmit={onSearch} className="search-bar">
        <Search size={16} />
        <input data-testid="contacts-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre, cedula, telefono..." />
      </form>
      <div className="filter-bar">
        <Filter size={16} />
        <select data-testid="filter-stage" value={filterStage} onChange={e => { setFilterStage(e.target.value); setPage(1); }}>
          <option value="">Todas las etapas</option>
          {Object.entries(STAGE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select data-testid="filter-origen" value={filterOrigen} onChange={e => { setFilterOrigen(e.target.value); setPage(1); }}>
          <option value="">Todos los origenes</option>
          {ORIGENES.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
      <div className="contacts-actions">
        <button data-testid="export-csv-button" className="btn-secondary" onClick={onExport}><Download size={16} /> Exportar CSV</button>
        <button data-testid="new-contact-button" className="btn-primary" onClick={onNewLead}><Plus size={16} /> Nuevo Lead</button>
      </div>
    </div>
  );
}

function ContactsTable({ leads, loading, onSelectLead }) {
  if (loading) return <table data-testid="contacts-table" className="contacts-table"><tbody><tr><td colSpan={9} className="table-loading">Cargando...</td></tr></tbody></table>;
  if (leads.length === 0) return <table data-testid="contacts-table" className="contacts-table"><tbody><tr><td colSpan={9} className="table-empty">No se encontraron contactos</td></tr></tbody></table>;

  return (
    <table data-testid="contacts-table" className="contacts-table">
      <thead>
        <tr>
          <th>Nombre</th><th>Cedula</th><th>Telefono</th><th>Origen</th>
          <th>Modelo / Plan</th><th>Agente</th><th>Etapa</th><th>Score</th><th>Acciones</th>
        </tr>
      </thead>
      <tbody>
        {leads.map(lead => {
          const scoreColor = lead.score >= 70 ? 'green' : lead.score >= 40 ? 'amber' : 'red';
          return (
            <tr key={lead.id} data-testid={`contact-row-${lead.id}`}>
              <td className="td-name">{lead.nombre} {lead.apellido}</td>
              <td>{lead.cedula}</td>
              <td>{lead.telefono}</td>
              <td>{lead.origen}</td>
              <td>{lead.modelo_interes || '-'}</td>
              <td>{lead.agente_nombre}</td>
              <td><span className={`stage-badge stage-${lead.etapa}`}>{STAGE_LABELS[lead.etapa]}</span></td>
              <td><span className={`score-dot score-${scoreColor}`}>{lead.score}</span></td>
              <td><button className="btn-icon" onClick={() => onSelectLead(lead)} title="Ver detalle"><Eye size={16} /></button></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function Pagination({ page, totalPages, total, count, setPage }) {
  return (
    <div className="contacts-pagination">
      <span>Mostrando {count} de {total} contactos</span>
      <div className="pagination-controls">
        <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={16} /></button>
        <span>Pagina {page} de {totalPages}</span>
        <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight size={16} /></button>
      </div>
    </div>
  );
}

export default function ContactsPage() {
  const leads = useCrmStore(s => s.leads);
  const totalLeads = useCrmStore(s => s.totalLeads);
  const loading = useCrmStore(s => s.loading);
  const fetchLeads = useCrmStore(s => s.fetchLeads);

  const [search, setSearch] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [filterOrigen, setFilterOrigen] = useState('');
  const [page, setPage] = useState(1);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showNewLead, setShowNewLead] = useState(false);
  const limit = 20;

  const doFetch = useCallback(() => {
    const p = { page, limit };
    if (search) p.search = search;
    if (filterStage) p.stage = filterStage;
    if (filterOrigen) p.origen = filterOrigen;
    fetchLeads(p);
  }, [page, limit, search, filterStage, filterOrigen, fetchLeads]);

  useEffect(() => { doFetch(); }, [doFetch]);

  const totalPages = useMemo(() => Math.ceil(totalLeads / limit) || 1, [totalLeads, limit]);

  const handleSearch = useCallback((e) => { e.preventDefault(); setPage(1); doFetch(); }, [doFetch]);

  const handleExportCSV = useCallback(() => {
    const headers = ['Nombre', 'Apellido', 'Cedula', 'Telefono', 'Email', 'Origen', 'Modelo', 'Etapa', 'Score', 'Agente'];
    const rows = leads.map(l => [l.nombre, l.apellido, l.cedula, l.telefono, l.email, l.origen, l.modelo_interes, l.etapa, l.score, l.agente_nombre]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'contactos_kompa.csv'; a.click();
    URL.revokeObjectURL(url);
  }, [leads]);

  return (
    <div data-testid="contacts-page" className="contacts-page">
      <ContactsToolbar
        search={search} setSearch={setSearch}
        filterStage={filterStage} setFilterStage={setFilterStage}
        filterOrigen={filterOrigen} setFilterOrigen={setFilterOrigen}
        setPage={setPage} onExport={handleExportCSV}
        onNewLead={() => setShowNewLead(true)} onSearch={handleSearch}
      />
      <div className="contacts-table-wrapper">
        <ContactsTable leads={leads} loading={loading} onSelectLead={setSelectedLead} />
      </div>
      <Pagination page={page} totalPages={totalPages} total={totalLeads} count={leads.length} setPage={setPage} />
      {selectedLead && <LeadDetailPanel lead={selectedLead} onClose={() => setSelectedLead(null)} onUpdate={doFetch} />}
      {showNewLead && <NewLeadModal onClose={() => setShowNewLead(false)} onCreated={doFetch} />}
    </div>
  );
}
